import { UseQueryOptions, useQuery } from '@tanstack/react-query';

import { useInvalidateIntegrations } from '@components/integrations';
import {
  EditPollingVisibilityType,
  IntegrationPollingType,
} from '@components/integrations/integrations-types';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import {
  editPollingVisibility,
  generateJqQuery,
  listPollingEndpoints,
  listSyncTestRequest,
  loadIntegrationJourneys,
  loadIntegrationTravellerActionFile,
  loadJourneyIntegrationEndpoints,
  loadJourneyIntegrationWebhooks,
  loadTravellerActionIntegrationEndpoint,
  loadTravellerJourneyIntegrationEndpoint,
  validateJqQuery,
} from './requests';

export const useJourneyIntegrationEndpoints = (shortId: string) => {
  return useQuery({
    queryKey: ['journey', shortId, 'integration-endpoints'],
    queryFn: () => loadJourneyIntegrationEndpoints(shortId),
    enabled: Boolean(shortId),
  });
};

export const useIntegrationJourneys = (shortId: string) => {
  return useQuery({
    queryKey: ['integration-journeys', shortId],
    queryFn: () => loadIntegrationJourneys(shortId),
    enabled: Boolean(shortId),
  });
};

export const useTravellerJourneyIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-journey-integration-endpoints', shortId],
    queryFn: () => loadTravellerJourneyIntegrationEndpoint(shortId),
  });
};

export const useTravellerActionIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-action-integration-endpoints', shortId],
    queryFn: () => loadTravellerActionIntegrationEndpoint(shortId),
  });
};

export const useJourneyIntegrationWebhook = (shortId: string) => {
  return useQuery({
    queryKey: ['journey', shortId, 'integration-webhooks'],
    queryFn: () => loadJourneyIntegrationWebhooks(shortId),
    enabled: Boolean(shortId),
  });
};

export const useIntegrationTravellerActionFile = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-action-integration-file', shortId],
    queryFn: () => loadIntegrationTravellerActionFile(shortId),
    enabled: Boolean(shortId),
  });
};

export const useListSyncTestRequest = (integrationShortId: string) => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (data: { url: string }) => listSyncTestRequest(integrationShortId, data),
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useEditPollingVisibility = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: EditPollingVisibilityType) => editPollingVisibility(data),
    onSuccess: () => {
      invalidateIntegrations();
      showSuccess(t('pollVisibilitySuccess'));
    },
  });
};

export const useListPollingEndpoints = (
  journeyId: string,
  options?: UseQueryOptions<IntegrationPollingType[]>
) => {
  return useQuery({
    queryKey: ['integrations', journeyId, 'polling-endpoints'],
    queryFn: () => listPollingEndpoints(journeyId),
    keepPreviousData: true,
    onSuccess: (integrations) => {
      options?.onSuccess?.(integrations);
    },
  });
};

export const useValidateJqQuery = () => {
  return useMutation({
    mutationFn: validateJqQuery,
  });
};

export const useGenerateJqQuery = () => {
  return useMutation({
    mutationFn: generateJqQuery,
  });
};

const locale = {
  en: {
    pollVisibilitySuccess: 'Polling visibility updated successfully',
  },
};
